import React, { useCallback, useMemo, useState } from 'react';

import { verified } from '@/assets/icons';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { Loader } from '@/components/shared/Loader';
import { getRotationStatusClassName } from '@/components/shared/RotationStatusLabel';
import {
	SettingCard,
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import {
	useDeleteSetlistMutation,
	useDeleteTeamSongMutation,
	useUpdateMyTeamMutation,
} from '@/graphql';
import { sentenceCase } from '@/helpers/changeCase';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { SongData } from '@/providers/AnalyticsProvider/events';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { Divider, List, SvgIcon } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { ESongModalDisplayState } from '../../../SongModalPanel';
import {
	ECreateSetlistDisplayState,
	IPropsCreateSetlistDisplay,
} from '../../CreateSetlistPanel';
import styles from './SetlistSettings.module.scss';

export const SetlistSettings: React.FC<IPropsCreateSetlistDisplay> = ({
	config,
	handleClose,
	setlistData,
}) => {
	const { mutateAsync: deleteSetlist, isLoading } =
		useDeleteSetlistMutation();
	const { refetch } = useSetlists();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();
	const [actionOpen, setActionOpen] = useState(false);

	const fireDeleteSetlist = useAnalyticsEvent('View_Setlist_360_Delete');

	const handleDialogClose = () => setActionOpen(false);

	const handleRemove = useCallback(async () => {
		if (setlistData?.id) {
			try {
				await deleteSetlist({
					setlistId: setlistData.id,
				});
				refetch();
				enqueueSnackbar('Draft setlist deleted');
				fireDeleteSetlist();
				handleClose({ removed: true });
			} catch (error) {
				handleError({
					errorCode: 'SetlistDraftError',
					error,
					friendlyMessage: `There was an issue with deleting your draft`,
				});
			}
		}
	}, [
		setlistData?.id,
		deleteSetlist,
		refetch,
		enqueueSnackbar,
		fireDeleteSetlist,
		handleClose,
		handleError,
	]);

	const isLoaded = !isLoading && !!setlistData && !!setlistData.startAt;
	const startAt = isLoaded ? new Date(setlistData.startAt) : new Date();
	const time = isLoaded ? format(startAt, 'h:mm a') : '-:--';
	const month = isLoaded ? format(startAt, 'MMM') : '...';
	const day = isLoaded ? format(startAt, 'd') : '...';

	// const additionalItems: SettingsListItems = [
	// 	{
	// 		type: 'title',
	// 		title: 'Song Details',
	// 	},
	// 	{
	// 		type: 'setting',
	// 		title: 'Song information',
	// 		onClick: () => {
	// 			if (isSynced) {
	// 				return setActionSyncedInfoOpen(true);
	// 			}
	// 			pushDisplayState(ESongModalDisplayState.SONG_INFORMATION);
	// 			fireViewSongInformationEvent();
	// 		},
	// 		trailingText: isSynced ? '' : openedSong.name,
	// 		chevronComponent: isSynced ? (
	// 			<SvgIcon
	// 				component={verified}
	// 				className={styles['verified-icon']}
	// 			/>
	// 		) : undefined,
	// 	},
	// ];

	return (
		<>
			{/* <SettingsList
				className={styles['song-settings__upper-list']}
				items={additionalItems}
			/>
			<Divider /> */}
			<List className={styles['song-settings__lower-list']}>
				<List>
					<SettingCard
						type="setting"
						title="Delete setlist"
						onClick={() => setActionOpen(true)}
						red
						noChevron
						loading={isLoading}
					/>
				</List>
			</List>
			<Divider />

			<ActionSheet
				open={actionOpen}
				title={
					<span>
						Are you sure you want to delete the setlist for{' '}
						<strong>
							{day}, {month}
						</strong>{' '}
						at <strong>{time}</strong>?
					</span>
				}
				onClose={handleDialogClose}
			>
				<ActionSheetAction
					label={'Remove for everyone'}
					onActionClick={() => {
						handleRemove();
					}}
					type={'danger'}
				/>
			</ActionSheet>
		</>
	);
};
